import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Close, CloudOff, CloudDone, DeleteOutline, Undo } from '@mui/icons-material';
import type { LiveEventType } from '@/api/generated/model';
import { ErrorState, LoadingState, ResponsiveDialog } from '@/components';
import { matchEventIcon, matchEventLabel } from '@/components/matchEventVisuals';
import { ActionButton, useMatchActionRunner } from '@/components/manage';
import { useMatchEvents } from '@/hooks/useApi';
import { useMatchLiveConfig } from '@/hooks/useManage';
import { useLiveEventQueue } from '@/hooks/useLiveEventQueue';
import { useNotification } from '@/contexts/NotificationContext';
import { getApiErrorMessage } from '@/utils/apiError';
import type { MatchEvent, Player } from '@/types';

type Side = 'home' | 'away';

const playerName = (p: Player) => [p.firstName, p.lastName].filter(Boolean).join(' ');

const parseMinuteSec = (value: string): number | undefined => {
  const m = Number.parseInt(value, 10);
  return Number.isFinite(m) && m >= 0 ? m * 60 : undefined;
};

const sideOf = (e: MatchEvent): Side | undefined => {
  const side = e.meta?.teamSide;
  return side === 'home' || side === 'away' ? side : undefined;
};

type Pair = { home: number; away: number };

const scoringValue = (meta?: Record<string, unknown>) => {
  const raw = Number(meta?.value);
  return Number.isFinite(raw) && raw > 0 ? raw : 1;
};

const scoresByPeriod = (events: MatchEvent[], scoringType?: string): Record<number, Pair> => {
  const out: Record<number, Pair> = {};
  for (const e of events) {
    const side = sideOf(e);
    if (!side || !scoringType || e.type !== scoringType || e.meta?.varCancelled) continue;
    const p = e.period && e.period > 0 ? e.period : 1;
    const cur = out[p] ?? { home: 0, away: 0 };
    cur[side] += scoringValue(e.meta);
    out[p] = cur;
  }
  return out;
};

const sumPairs = (byPeriod: Record<number, Pair>): Pair => {
  const total = { home: 0, away: 0 };
  for (const pair of Object.values(byPeriod)) {
    total.home += pair.home;
    total.away += pair.away;
  }
  return total;
};

const padSx = (side: Side) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  borderRadius: 2,
  bgcolor: side === 'home' ? 'rgba(124, 77, 255, 0.16)' : 'rgba(0, 170, 255, 0.16)',
  minHeight: { xs: 200, sm: 260 },
  p: 2,
});

const GoalLayout: React.FC<{
  sideName: (side: Side) => string;
  score: Pair;
  label: string;
  canEdit: boolean;
  onScore: (side: Side) => void;
}> = ({ sideName, score, label, canEdit, onScore }) => (
  <Box sx={{ display: 'flex', gap: 1, px: 1 }}>
    {(['home', 'away'] as Side[]).map((side) => (
      <ButtonBase
        key={side}
        onClick={() => onScore(side)}
        disabled={!canEdit}
        aria-label={`${label}: ${sideName(side)}`}
        sx={padSx(side)}
      >
        <Typography variant="h4" textAlign="center" sx={{ wordBreak: 'break-word' }}>
          {sideName(side)}
        </Typography>
        <Typography sx={{ fontSize: { xs: '4.5rem', sm: '6rem' }, fontWeight: 800, lineHeight: 1.1 }}>{score[side]}</Typography>
        {canEdit && (
          <Typography variant="caption" color="text.secondary">
            Нажмите: +{label.toLowerCase()}
          </Typography>
        )}
      </ButtonBase>
    ))}
  </Box>
);

const RallyLayout: React.FC<{
  sideName: (side: Side) => string;
  byPeriod: Record<number, Pair>;
  period: number;
  label: string;
  canEdit: boolean;
  onScore: (side: Side) => void;
}> = ({ sideName, byPeriod, period, label, canEdit, onScore }) => {
  const cur = byPeriod[period] ?? { home: 0, away: 0 };
  const last = Math.max(period, ...Object.keys(byPeriod).map(Number), 1);
  const line = Array.from({ length: last }, (_, i) => {
    const pair = byPeriod[i + 1] ?? { home: 0, away: 0 };
    return `${pair.home}:${pair.away}`;
  }).join(' · ');
  return (
    <>
      <GoalLayout sideName={sideName} score={cur} label={label} canEdit={canEdit} onScore={onScore} />
      <Typography textAlign="center" sx={{ pt: 1, fontWeight: 700 }}>
        {line}
      </Typography>
    </>
  );
};

const PointsLayout: React.FC<{
  sideName: (side: Side) => string;
  score: Pair;
  values: number[];
  canEdit: boolean;
  onScore: (side: Side, value: number) => void;
}> = ({ sideName, score, values, canEdit, onScore }) => (
  <Box sx={{ display: 'flex', gap: 1, px: 1 }}>
    {(['home', 'away'] as Side[]).map((side) => (
      <Box key={side} sx={padSx(side)}>
        <Typography variant="h4" textAlign="center" sx={{ wordBreak: 'break-word' }}>
          {sideName(side)}
        </Typography>
        <Typography sx={{ fontSize: { xs: '4.5rem', sm: '6rem' }, fontWeight: 800, lineHeight: 1.1 }}>{score[side]}</Typography>
        {canEdit && (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {values.map((value) => (
              <Button
                key={value}
                variant="contained"
                color="manage"
                onClick={() => onScore(side, value)}
                sx={{ minHeight: 48, minWidth: 56, fontSize: '1.25rem' }}
              >
                +{value}
              </Button>
            ))}
          </Stack>
        )}
      </Box>
    ))}
  </Box>
);

/** Удерживает экран включённым, пока идёт матч. */
const useWakeLock = (active: boolean) => {
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    const request = async () => {
      try {
        lock = await navigator.wakeLock.request('screen');
      } catch {
        /* браузер отказал (энергосбережение) — работаем без блокировки */
      }
    };
    const onVisible = () => document.visibilityState === 'visible' && void request();
    void request();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      void lock?.release();
    };
  }, [active]);
};

type EventFlags = { needsPlayer: boolean; needsMinute: boolean; allowOwnGoal: boolean; allowVar: boolean };

type PlayerSheet = {
  side: Side;
  eventId?: string;
  type?: LiveEventType;
  needsPlayer: boolean;
  needsMinute: boolean;
  allowOwnGoal: boolean;
  value?: number;
  title: string;
} | null;

interface EventEditorProps {
  event: MatchEvent | null;
  types: { type: string; label: string }[];
  flagsFor: (type: string) => EventFlags;
  periods: number;
  periodLabel?: string;
  sideName: (side: Side) => string;
  lineup: (side: Side) => Player[];
  onSave: (event: MatchEvent, patch: Partial<MatchEvent>) => void;
  onDelete: (event: MatchEvent) => void;
  onClose: () => void;
}

/** Шторка правки события: тип, команда, игрок, минута, период, отмена VAR, удаление. */
const EventEditor: React.FC<EventEditorProps> = ({
  event,
  types,
  flagsFor,
  periods,
  periodLabel,
  sideName,
  lineup,
  onSave,
  onDelete,
  onClose,
}) => {
  const [draft, setDraft] = useState<MatchEvent | null>(event);
  const [minute, setMinute] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [current, setCurrent] = useState(event);
  if (event !== current) {
    setCurrent(event);
    setDraft(event);
    setMinute(event?.timestampSec != null ? String(Math.floor(event.timestampSec / 60)) : '');
    setConfirmDelete(false);
  }

  const side = draft ? sideOf(draft) : undefined;
  const flags = draft ? flagsFor(draft.type) : undefined;
  const isOwnGoal = Boolean(flags?.allowOwnGoal && draft?.meta?.ownGoal);
  const actorSide: Side | undefined = isOwnGoal && side ? (side === 'home' ? 'away' : 'home') : side;
  const set = (patch: Partial<MatchEvent>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const setMeta = (patch: Record<string, unknown>) => setDraft((d) => (d ? { ...d, meta: { ...d.meta, ...patch } } : d));

  const save = () => {
    if (!event || !draft) return;
    const next = flagsFor(draft.type);
    const m = Number.parseInt(minute, 10);
    const meta = { ...(draft.meta ?? {}) };
    if (!(next.allowOwnGoal && meta.ownGoal === true)) delete meta.ownGoal;
    if (!next.allowVar) delete meta.varCancelled;
    onSave(event, {
      type: draft.type,
      period: draft.period ?? null,
      actorId: draft.actorId ?? null,
      timestampSec: next.needsMinute ? (Number.isFinite(m) && m >= 0 ? m * 60 : null) : (draft.timestampSec ?? null),
      meta,
    });
  };

  return (
    <ResponsiveDialog open={Boolean(event)} onClose={onClose} title="Событие">
      {draft && (
        <Stack spacing={2}>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {types.map((t) => (
              <Chip
                key={t.type}
                label={t.label}
                color={draft.type === t.type ? 'manage' : 'default'}
                variant={draft.type === t.type ? 'filled' : 'outlined'}
                icon={<Box sx={{ display: 'flex', alignItems: 'center' }}>{matchEventIcon(t.type)}</Box>}
                onClick={() =>
                  setDraft((d) => {
                    if (!d) return d;
                    const meta = { ...d.meta };
                    if (!flagsFor(t.type).allowOwnGoal) delete meta.ownGoal;
                    return { ...d, type: t.type as MatchEvent['type'], meta };
                  })
                }
                sx={{ height: 40 }}
              />
            ))}
          </Stack>

          <Stack direction="row" spacing={1}>
            {(['home', 'away'] as Side[]).map((s) => (
              <Chip
                key={s}
                label={sideName(s)}
                color={side === s ? 'manage' : 'default'}
                variant={side === s ? 'filled' : 'outlined'}
                onClick={() => {
                  if (s !== side) setDraft((d) => (d ? { ...d, actorId: null, meta: { ...d.meta, teamSide: s } } : d));
                }}
                sx={{ height: 40, flex: 1 }}
              />
            ))}
          </Stack>
          {isOwnGoal && (
            <Typography variant="caption" color="text.secondary">
              Кому засчитали гол. Автора выбирайте из состава соперника.
            </Typography>
          )}

          {flags?.allowOwnGoal && (
            <FormControlLabel
              control={
                <Switch
                  checked={isOwnGoal}
                  onChange={(_, v) => setDraft((d) => (d ? { ...d, actorId: null, meta: { ...d.meta, ownGoal: v } } : d))}
                />
              }
              label="Автогол"
            />
          )}

          <Stack direction="row" spacing={1} alignItems="center">
            {flags?.needsMinute && (
              <TextField
                label="Минута"
                type="number"
                value={minute}
                onChange={(e) => setMinute(e.target.value)}
                slotProps={{ htmlInput: { min: 0, inputMode: 'numeric' } }}
                sx={{ width: 120 }}
              />
            )}
            {periods > 1 &&
              Array.from({ length: periods }, (_, i) => i + 1).map((p) => (
                <Chip
                  key={p}
                  label={`${p} ${periodLabel ?? 'период'}`}
                  color={draft.period === p ? 'manage' : 'default'}
                  variant={draft.period === p ? 'filled' : 'outlined'}
                  onClick={() => set({ period: p })}
                  sx={{ height: 40 }}
                />
              ))}
          </Stack>

          {flags?.needsPlayer && actorSide && lineup(actorSide).length > 0 && (
            <TextField
              select
              label={isOwnGoal ? 'Автор автогола' : 'Игрок'}
              value={draft.actorId ?? ''}
              onChange={(e) => set({ actorId: e.target.value || null })}
            >
              <MenuItem value="">Не указан</MenuItem>
              {lineup(actorSide).map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {playerName(p)}
                </MenuItem>
              ))}
            </TextField>
          )}

          {flags?.allowVar && (
            <FormControlLabel
              control={<Switch checked={draft.meta?.varCancelled === true} onChange={(_, v) => setMeta({ varCancelled: v })} />}
              label="Отменено VAR"
            />
          )}

          <Stack direction="row" spacing={1}>
            {confirmDelete ? (
              <Button color="error" variant="contained" onClick={() => onDelete(event!)} sx={{ minHeight: 48, flex: 1 }}>
                Точно удалить
              </Button>
            ) : (
              <Button color="error" variant="outlined" startIcon={<DeleteOutline />} onClick={() => setConfirmDelete(true)} sx={{ minHeight: 48 }}>
                Удалить
              </Button>
            )}
            <Button variant="contained" color="manage" onClick={save} sx={{ minHeight: 48, flex: 1 }}>
              Сохранить
            </Button>
          </Stack>
        </Stack>
      )}
    </ResponsiveDialog>
  );
};

interface PlayerPickSheetProps {
  open: boolean;
  title: string;
  players: Player[];
  oppositePlayers: Player[];
  needsPlayer: boolean;
  needsMinute: boolean;
  allowOwnGoal: boolean;
  onDone: (player?: Player, timestampSec?: number, ownGoal?: boolean) => void;
  onClose: () => void;
}

/** Шторка гола: минута, автогол, игрок. Подтверждение — отдельной кнопкой. */
const PlayerPickSheet: React.FC<PlayerPickSheetProps> = ({
  open,
  title,
  players,
  oppositePlayers,
  needsPlayer,
  needsMinute,
  allowOwnGoal,
  onDone,
  onClose,
}) => {
  const [minute, setMinute] = useState('');
  const [ownGoal, setOwnGoal] = useState(false);
  const [playerId, setPlayerId] = useState('');
  const minuteRef = useRef<HTMLInputElement>(null);
  const list = ownGoal ? oppositePlayers : players;

  useEffect(() => {
    if (!open || !needsMinute) return;
    const t = window.setTimeout(() => minuteRef.current?.focus(), 200);
    return () => window.clearTimeout(t);
  }, [open, needsMinute]);

  const finish = () => onDone(list.find((p) => p.id === playerId), parseMinuteSec(minute), ownGoal);

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button onClick={onClose} sx={{ minHeight: 48 }}>
            Отменить
          </Button>
          <Button variant="contained" color="manage" onClick={finish} sx={{ minHeight: 48 }}>
            Подтвердить
          </Button>
        </>
      }
    >
      <Stack spacing={2}>
        {allowOwnGoal && (
          <FormControlLabel
            control={
              <Switch
                checked={ownGoal}
                onChange={(_, v) => {
                  setOwnGoal(v);
                  setPlayerId('');
                }}
              />
            }
            label="Автогол"
          />
        )}
        {ownGoal && (
          <Typography variant="caption" color="text.secondary">
            Кому засчитали гол. Автора выбирайте из состава соперника.
          </Typography>
        )}
        {needsMinute && (
          <TextField
            label="Минута"
            type="number"
            value={minute}
            onChange={(e) => setMinute(e.target.value)}
            inputRef={minuteRef}
            autoFocus
            slotProps={{ htmlInput: { min: 0, inputMode: 'numeric' } }}
            sx={{ width: 120 }}
          />
        )}
        {needsPlayer && (
          <TextField
            select
            label={ownGoal ? 'Автор автогола' : 'Игрок'}
            value={playerId}
            onChange={(e) => setPlayerId(e.target.value)}
          >
            <MenuItem value="">Не указан</MenuItem>
            {list.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {playerName(p)}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>
    </ResponsiveDialog>
  );
};

export const MatchLivePage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showError } = useNotification();
  const { data: config, isLoading, error, refetch } = useMatchLiveConfig(id);
  const { data: serverEvents = [] } = useMatchEvents(id, { queryKey: ['matches', id, 'events'], refetchInterval: 15_000 });
  const onRejected = useCallback((err: unknown) => showError(getApiErrorMessage(err, 'Событие не сохранено')), [showError]);
  const queue = useLiveEventQueue(id, onRejected);
  const [period, setPeriod] = useState(1);
  const [sheet, setSheet] = useState<PlayerSheet>(null);
  const [editing, setEditing] = useState<MatchEvent | null>(null);
  const [confirmUndo, setConfirmUndo] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);

  const returnHref = config?.returnHref || `/matches/${id}`;
  const onActionDone = useCallback(
    (actionId: string) => {
      void refetch();
      if (actionId === 'finish' && config?.resultConfirmation === 'none') navigate(returnHref);
    },
    [config?.resultConfirmation, navigate, refetch, returnHref],
  );
  const runAction = useMatchActionRunner(id, onActionDone);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  const isLive = config?.actions.some((a) => a.id === 'finish' || a.id === 'confirm') ?? false;
  useWakeLock(isLive);

  const events = useMemo(() => queue.merge(serverEvents), [queue, serverEvents]);
  const byPeriod = useMemo(() => scoresByPeriod(events, config?.scoring.event), [events, config?.scoring.event]);
  const total = useMemo(() => sumPairs(byPeriod), [byPeriod]);

  if (isLoading) return <LoadingState fullscreen message="Загрузка матча..." />;
  if (error || !config) return <ErrorState message="Не удалось открыть ведение матча" onRetry={refetch} />;

  const lineup = (side: Side) => (side === 'home' ? config.homeLineup : config.awayLineup) ?? [];
  const sideName = (side: Side) => (side === 'home' ? config.homeName : config.awayName);
  const canEdit = isLive && Boolean(config.permissions?.reportResult);

  const scoring = config.scoring;
  const values = scoring.values?.length ? scoring.values : [1];
  const flagsFor = (type: string): EventFlags => {
    if (type === scoring.event) {
      return {
        needsPlayer: scoring.needsPlayer,
        needsMinute: scoring.needsMinute,
        allowOwnGoal: scoring.allowOwnGoal,
        allowVar: scoring.allowVar,
      };
    }
    const quick = config.quickEvents.find((item) => item.type === type);
    return {
      needsPlayer: Boolean(quick?.needsPlayer),
      needsMinute: Boolean(quick?.needsMinute),
      allowOwnGoal: false,
      allowVar: false,
    };
  };

  const addEvent = (side: Side, type: string, extra?: { actorId?: string; timestampSec?: number; value?: number }) =>
    queue.add({
      type: type as MatchEvent['type'],
      period,
      actorId: extra?.actorId ?? null,
      timestampSec: extra?.timestampSec ?? null,
      meta: { teamSide: side, ...(extra?.value != null ? { value: extra.value } : {}) },
    });

  const openSheet = (
    side: Side,
    flags: { needsPlayer: boolean; needsMinute: boolean; allowOwnGoal?: boolean },
    title: string,
    extra?: { eventId?: string; type?: LiveEventType; value?: number },
  ) => {
    const other: Side = side === 'home' ? 'away' : 'home';
    const needsPlayer = Boolean(
      flags.needsPlayer && (lineup(side).length > 0 || (flags.allowOwnGoal && lineup(other).length > 0)),
    );
    if (!needsPlayer && !flags.needsMinute && !flags.allowOwnGoal) return;
    setSheet({
      side,
      eventId: extra?.eventId,
      type: extra?.type,
      value: extra?.value,
      needsPlayer,
      needsMinute: flags.needsMinute,
      allowOwnGoal: Boolean(flags.allowOwnGoal),
      title,
    });
  };

  const onScore = (side: Side, value: number) => {
    if (!canEdit) return;
    const eventId = addEvent(side, scoring.event, { value });
    openSheet(side, scoring, sideName(side), { eventId, value });
  };

  const closeSheet = (cancel = false) => {
    if (cancel && sheet?.eventId) queue.remove(sheet.eventId);
    setSheet(null);
  };

  const pickPlayer = (player?: Player, timestampSec?: number, ownGoal?: boolean) => {
    if (!sheet) return;
    const meta = {
      teamSide: sheet.side,
      ...(sheet.value != null ? { value: sheet.value } : {}),
      ...(ownGoal ? { ownGoal: true } : {}),
    };
    const patch = {
      actorId: player?.id ?? null,
      ...(timestampSec != null ? { timestampSec } : {}),
      meta,
    };
    if (sheet.eventId) {
      queue.update(sheet.eventId, patch);
    } else if (sheet.type) {
      addEvent(sheet.side, sheet.type.type, { actorId: player?.id, timestampSec });
    }
    setSheet(null);
  };

  const quickEvent = (type: LiveEventType, side: Side) => {
    const needsPlayer = Boolean(type.needsPlayer);
    const needsMinute = Boolean(type.needsMinute);
    if ((needsPlayer && lineup(side).length > 0) || needsMinute) {
      openSheet(side, { needsPlayer, needsMinute }, `${type.label}: ${sideName(side)}`, { type });
      return;
    }
    addEvent(side, type.type);
  };

  const lastEvent = events[events.length - 1];
  const labelOf = (e: MatchEvent) =>
    matchEventLabel(e.type, e.meta, e.type === scoring.event ? scoring.label : config.quickEvents.find((q) => q.type === e.type)?.label);
  const actorName = (e: MatchEvent) => {
    const all = [...(config.homeLineup ?? []), ...(config.awayLineup ?? [])];
    const p = all.find((x) => x.id === e.actorId);
    return p ? playerName(p) : undefined;
  };

  const layout =
    config.layout === 'rally' ? (
      <RallyLayout sideName={sideName} byPeriod={byPeriod} period={period} label={scoring.label} canEdit={canEdit} onScore={(side) => onScore(side, values[0] ?? 1)} />
    ) : config.layout === 'points' ? (
      <PointsLayout sideName={sideName} score={total} values={values} canEdit={canEdit} onScore={onScore} />
    ) : (
      <GoalLayout sideName={sideName} score={total} label={scoring.label} canEdit={canEdit} onScore={(side) => onScore(side, values[0] ?? 1)} />
    );

  return (
    <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', maxWidth: 900, mx: 'auto', width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, minHeight: 56, pt: 'env(safe-area-inset-top)' }}>
        <IconButton aria-label="Закрыть" onClick={() => navigate(returnHref)} sx={{ width: 48, height: 48 }}>
          <Close />
        </IconButton>
        <Typography variant="h4" sx={{ flex: 1 }} noWrap>
          Ведение матча
        </Typography>
        <Chip
          role="status"
          aria-live="polite"
          icon={queue.pending || !online ? <CloudOff /> : <CloudDone />}
          color={queue.pending || !online ? 'warning' : 'manage'}
          variant={queue.pending || !online ? 'filled' : 'outlined'}
          label={queue.pending ? `Не синхронизировано: ${queue.pending}` : online ? 'Сохранено' : 'Нет сети'}
          sx={{ height: 40 }}
        />
      </Box>

      {config.periods > 1 && (
        <Stack direction="row" spacing={1} sx={{ px: 2, pb: 1, overflowX: 'auto' }}>
          {Array.from({ length: config.periods }, (_, i) => i + 1).map((p) => (
            <Chip
              key={p}
              label={`${p} ${config.periodLabel ?? 'период'}`}
              color={p === period ? 'manage' : 'default'}
              variant={p === period ? 'filled' : 'outlined'}
              onClick={() => setPeriod(p)}
              sx={{ height: 40 }}
            />
          ))}
        </Stack>
      )}

      {layout}

      {canEdit && config.quickEvents.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ px: 1, pt: 1.5 }}>
          {config.quickEvents.map((q) =>
            (['home', 'away'] as Side[]).map((side) => (
              <Button
                key={`${q.type}-${side}`}
                variant="outlined"
                startIcon={matchEventIcon(q.type)}
                onClick={() => quickEvent(q, side)}
                sx={{ minHeight: 48, flex: '1 1 40%' }}
              >
                {q.label}: {sideName(side)}
              </Button>
            )),
          )}
        </Stack>
      )}

      <Box sx={{ flex: 1, px: 1, pt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="h5" sx={{ flex: 1 }}>
            События
          </Typography>
          {canEdit && lastEvent && (
            <Button startIcon={<Undo />} onClick={() => setConfirmUndo(true)} sx={{ minHeight: 48 }}>
              Отменить последнее
            </Button>
          )}
        </Box>
        {events.length === 0 ? (
          <Typography color="text.secondary">Событий пока нет</Typography>
        ) : (
          <List disablePadding>
            {[...events].reverse().map((e, index, list) => {
              const cancelled = e.meta?.varCancelled === true;
              const row = (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', opacity: cancelled ? 0.7 : 1 }}>
                  <Box sx={{ width: 28, minWidth: 28, display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
                    {matchEventIcon(e.type, e.meta)}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={cancelled ? { textDecoration: 'line-through', color: 'text.secondary' } : undefined}>
                      {labelOf(e)}
                      {Number(e.meta?.value) > 1 ? ` +${e.meta?.value}` : ''}
                      {e.meta?.ownGoal === true ? ' (автогол)' : ''}
                      {sideOf(e) ? ` ${sideName(sideOf(e)!)}` : ''}
                    </Typography>
                    {(actorName(e) || flagsFor(e.type).needsPlayer) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {actorName(e) ?? '—'}
                      </Typography>
                    )}
                    {(e.timestampSec != null || e.period) && (
                      <Typography variant="caption" color="text.secondary">
                        {e.timestampSec != null ? `${Math.floor(e.timestampSec / 60)}'` : ''}
                        {e.timestampSec != null && e.period ? ' • ' : ''}
                        {e.period ? `${e.period} ${config.periodLabel ?? 'тайм'}` : ''}
                      </Typography>
                    )}
                    {cancelled && (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                        Отменено по решению VAR
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
              return (
                <React.Fragment key={e.id}>
                  {canEdit ? (
                    <ListItemButton onClick={() => setEditing(e)} sx={{ minHeight: 56, px: 1 }}>
                      {row}
                    </ListItemButton>
                  ) : (
                    <ListItem disableGutters sx={{ px: 1 }}>
                      {row}
                    </ListItem>
                  )}
                  {index < list.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>

      <Stack spacing={1} sx={{ position: 'sticky', bottom: 0, p: 2, pb: 'calc(16px + env(safe-area-inset-bottom))', bgcolor: 'background.default' }}>
        {config.actions.map((a) => (
          <ActionButton key={a.id} action={a} run={runAction} fullWidth />
        ))}
      </Stack>

      {sheet && (
        <PlayerPickSheet
          open
          title={sheet.title}
          players={lineup(sheet.side)}
          oppositePlayers={lineup(sheet.side === 'home' ? 'away' : 'home')}
          needsPlayer={sheet.needsPlayer}
          needsMinute={sheet.needsMinute}
          allowOwnGoal={sheet.allowOwnGoal}
          onDone={pickPlayer}
          onClose={() => closeSheet(true)}
        />
      )}

      <EventEditor
        event={editing}
        types={[{ type: scoring.event, label: scoring.label }, ...config.quickEvents]}
        flagsFor={flagsFor}
        periods={config.periods}
        periodLabel={config.periodLabel}
        sideName={sideName}
        lineup={lineup}
        onSave={(e, patch) => {
          queue.update(e.id, patch);
          setEditing(null);
        }}
        onDelete={(e) => {
          queue.remove(e.id);
          setEditing(null);
        }}
        onClose={() => setEditing(null)}
      />

      <ResponsiveDialog
        open={confirmUndo}
        onClose={() => setConfirmUndo(false)}
        title="Отменить последнее?"
        maxWidth="xs"
        actions={
          <>
            <Button onClick={() => setConfirmUndo(false)}>Нет</Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => {
                if (lastEvent) queue.remove(lastEvent.id);
                setConfirmUndo(false);
              }}
            >
              Отменить
            </Button>
          </>
        }
      >
        <Typography color="text.secondary">
          {lastEvent
            ? `${labelOf(lastEvent)}${sideOf(lastEvent) ? ` · ${sideName(sideOf(lastEvent)!)}` : ''} будет удалено.`
            : 'Последнее событие будет удалено.'}
        </Typography>
      </ResponsiveDialog>
    </Box>
  );
};
